import { channelMetaDataKeys } from '@inbox-demo-nx/constants';
import { PlatformAPIChannel } from '@inbox-demo-nx/types';
import axios from 'axios';

import { chatAxios } from './axios';

export async function fetchGroupChannels({
  userId,
  params,
}: {
  userId: string;
  params?: { members_exactly_in?: string; custom_types?: string };
}): Promise<PlatformAPIChannel[]> {
  const { data } = await chatAxios.get(
    `/v3/users/${userId}/my_group_channels`,
    {
      params: { ...params, limit: 100, show_empty: true },
    }
  );
  return data.channels;
}

export async function getGroupChannel({
  channelUrl,
}: {
  channelUrl: string;
}): Promise<PlatformAPIChannel> {
  const { data } = await chatAxios.get(`/v3/group_channels/${channelUrl}`, {
    params: { show_member: true },
  });
  return data;
}

export async function updateGroupChannel(
  channelUrl: string,
  params: { custom_type?: string; data?: string }
): Promise<PlatformAPIChannel> {
  const { data } = await chatAxios.put(
    `/v3/group_channels/${channelUrl}`,
    params
  );
  return data;
}

export async function createGroupChannel(params: {
  channel_url?: string;
  cover_url: string;
  custom_type?: string;
  inviter_id?: string;
  is_distinct: boolean;
  name?: string;
  user_ids: string[];
  data?: string;
}): Promise<PlatformAPIChannel> {
  const { data } = await chatAxios.post('/v3/group_channels', params);
  return data;
}

export async function freezeGroupChannel({
  channelUrl,
}: {
  channelUrl: string;
}): Promise<PlatformAPIChannel> {
  const { data } = await chatAxios.put(
    `/v3/group_channels/${channelUrl}/freeze`,
    { freeze: true }
  );
  return data;
}

export async function getGroupChannelMetaDataOfKey({
  channelUrl,
  key,
}: {
  channelUrl: string;
  key: string;
}) {
  try {
    const { data } = await chatAxios.get(
      `/v3/group_channels/${channelUrl}/metadata/${encodeURIComponent(key)}`
    );
    return { value: data[key] as string };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if ((error.response?.data as { code: number })?.code === 400201) {
        return { value: null };
      }
    }
    throw error;
  }
}

export async function updateGroupChannelMetaDataOfKey({
  channelUrl,
  key,
  value,
}: {
  channelUrl: string;
  key: string;
  value: string;
}) {
  const { data } = await chatAxios.put(
    `/v3/group_channels/${channelUrl}/metadata/${encodeURIComponent(key)}`,
    { value, upsert: true }
  );
  return data;
}

export async function getGroupChannelMetaData({
  channelUrl,
}: {
  channelUrl: string;
}) {
  const { data } = await chatAxios.get(
    `/v3/group_channels/${channelUrl}/metadata`
  );
  return data;
}

export async function updateGroupChannelMetaData({
  channelUrl,
  metaData,
}: {
  channelUrl: string;
  metaData: Record<string, string>;
}) {
  const { data } = await chatAxios.put(
    `/v3/group_channels/${channelUrl}/metadata`,
    {
      metadata: metaData,
      upsert: true,
    }
  );
  return data;
}

export async function inviteUser({
  channelUrl,
  userId,
}: {
  channelUrl: string;
  userId: string;
}): Promise<PlatformAPIChannel> {
  const { data: channel } = await chatAxios.post(
    `/v3/group_channels/${channelUrl}/invite`,
    { user_ids: [userId] }
  );
  return channel;
}

export async function leaveChannel(channelUrl: string, userId: string) {
  await chatAxios.put(`/v3/group_channels/${channelUrl}/leave`, {
    user_ids: [userId],
  });
}

export async function deleteChannel({ channelUrl }: { channelUrl: string }) {
  return chatAxios.delete(`/v3/group_channels/${channelUrl}`);
}

export async function deleteChannelIfExists({
  channelUrl,
}: {
  channelUrl: string;
}) {
  try {
    const { data } = await chatAxios.delete(`/v3/group_channels/${channelUrl}`);
    return data;
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      (error.response?.data as any)?.['code'] === 400201
    ) {
      return { message: 'Channel does not exist' };
    } else {
      throw error;
    }
  }
}

export async function deleteAllGroupChannels({ userId }: { userId: string }) {
  const channels = await fetchGroupChannels({ userId });
  await Promise.all(
    channels.map((channel: { channel_url: string }) => {
      return deleteChannel({ channelUrl: channel.channel_url });
    })
  );
}

export async function getChannelState({ channelUrl }: { channelUrl: string }) {
  try {
    const { value } = await getGroupChannelMetaDataOfKey({
      channelUrl,
      key: channelMetaDataKeys.state,
    });
    return { state: value };
  } catch (error) {
    return { state: null };
  }
}

export async function getChannelUrlByCustomType({
  userId,
  customType,
}: {
  userId: string;
  customType: string;
}) {
  const [channel] = await fetchGroupChannels({
    userId,
    params: {
      custom_types: customType,
    },
  });
  return { channelUrl: channel.channel_url };
}
